import React, { useEffect, useState } from "react";
import { Box } from "@mantine/core";
import { motion, wrap } from "framer-motion";
import { ThemeType } from "../../shared/util/types";
import { AllGuessesInTypeTriviaLies, GuessResultsType } from "./types";
import DynamicText from "../../shared/components/DynamicText";
import PlayerReminder from "../../shared/components/PlayerReminder";

type ResultPresentationProps = {
  theme: ThemeType;
  round: string;
  results?: AllGuessesInTypeTriviaLies;
  hostMessage: (msg: string) => void;
};

const ResultPresentation = ({
  theme,
  round,
  results,
  hostMessage,
}: ResultPresentationProps) => {
  const timeBetweenCaptionsSec = 6.0;
  const [presentingCaption, setPresentingCaption] = useState<
    GuessResultsType | undefined
  >(undefined);
  const [currentCaptionKey, setCurrentCaptionKey] = useState<string>("init");
  const [state, setState] = useState("ShowGuesses");
  const getReminder = () => {
    const numPlayersPickedThis =
      (presentingCaption !== undefined &&
        presentingCaption.PlayersThatGuessedThis.length) ||
      0;
    const player = presentingCaption?.Owner || "";
    const isTruth = presentingCaption?.IsTruth || false;
    const isHalfPoints = presentingCaption?.IsHalfPoints || false;
    const pointsPerGuess =
      (presentingCaption?.TotalPoints || 1) / (isHalfPoints ? 2 : 1) || 1;
    return isTruth
      ? numPlayersPickedThis > 0
        ? pointsPerGuess +
          " Points for Picking the Truth, " +
          pointsPerGuess * numPlayersPickedThis +
          " Points to " +
          player
        : "Nobody Picked the Truth!"
      : (numPlayersPickedThis > 0
          ? pointsPerGuess * numPlayersPickedThis +
            " Points to " +
            player +
            " for fooling " +
            numPlayersPickedThis +
            (numPlayersPickedThis > 1 ? " people!" : " person!")
          : "Nobody picked " + player + "'s Lie :/") +
          (isHalfPoints || false ? "(half points)" : "");
  };
  useEffect(() => {
    const newResultsRoutine = async (results: AllGuessesInTypeTriviaLies) => {
      for (let i = 0; i < results.Lies.length; i++) {
        if (results.Lies[i].PlayersThatGuessedThis.length > 0) {
          setPresentingCaption(results.Lies[i]);
          setCurrentCaptionKey("" + i);
          await new Promise((resolve) =>
            setTimeout(resolve, timeBetweenCaptionsSec * 1000)
          );
        }
      }
      setPresentingCaption(results.Truth);
      setCurrentCaptionKey("truth");
      await new Promise((resolve) =>
        setTimeout(resolve, timeBetweenCaptionsSec * 1000)
      );
      setState("ShowScores");
      await new Promise((resolve) =>
        setTimeout(resolve, timeBetweenCaptionsSec * 1000)
      );
      await hostMessage("ShowResultsComplete");
    };
    setState("ShowGuesses");
    results && newResultsRoutine(results);
  }, [results, hostMessage]);
  const showGuesses = () => {
    return (
      <Box
        style={{
          display: "flex",
          flexDirection: "row",
          height: "100%",
          alignContent: "center",
          alignItems: "center",
        }}
      >
        {results && (
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              alignContent: "center",
              width: "100% ",
              alignItems: "center",
            }}
          >
            <motion.div
              key={"caption" + currentCaptionKey}
              animate={{ x: ["calc(150vw)", 0, 0, "calc(150vw)"] }}
              transition={{
                times: [0, 0.2, 0.9, 1],
                duration: timeBetweenCaptionsSec,
                ease: "easeInOut",
              }}
            >
              <DynamicText
                style={{
                  display: "flex",
                  color: theme.TextColor,
                  border: "2px solid white",
                  width: "calc(40vw)",
                  borderRadius: "15px",
                  fontSize: 30,
                  paddingLeft: 20,
                  paddingRight: 20,
                  paddingTop: 5,
                  paddingBottom: 10,
                  marginLeft: -20,
                  marginBottom: 10,
                  marginTop: 0,
                  marginRight: 0,
                  wrap,
                }}
              >
                {presentingCaption && presentingCaption.TruthOrLieText}
              </DynamicText>
            </motion.div>
            {presentingCaption &&
              presentingCaption.PlayersThatGuessedThis.map((p, i) => {
                return (
                  <motion.div
                    key={"caption-guess" + i + currentCaptionKey}
                    animate={{ y: ["calc(110vh)", 0, 0, "calc(110vh)"] }}
                    transition={{
                      times: [0, 0.2, 0.9, 1],
                      delay: 0.3 * (i + 1),
                      duration: timeBetweenCaptionsSec - 1,
                      ease: ["easeInOut"],
                    }}
                  >
                    <DynamicText
                      style={{
                        border: "2px solid white",
                        width: "calc(30vw)",
                        borderRadius: 20,
                        fontSize: 20,
                        alignContent: "center",
                        textAlign: "center",
                        justifyContent: "center",
                        justifyItems: "center",
                        paddingLeft: 20,
                        paddingRight: 20,
                        paddingTop: 5,
                        paddingBottom: 5,
                        marginLeft: -20,
                        marginBottom: 10,
                        marginTop: 0,
                        marginRight: 0,
                      }}
                    >
                      {p}
                    </DynamicText>
                  </motion.div>
                );
              })}
          </Box>
        )}
      </Box>
    );
  };
  const showScores = () => {
    if (!results) {
      return null;
    }
    const sorted = results.PlayerPoints.sort((a, b) => b.Points - a.Points);
    return (
      <>
        {sorted.map((p, i) => {
          return (
            <motion.div
              key={"score" + i}
              initial={{ y: "calc(110vh)" }}
              animate={{ y: 0 }}
              exit={{ y: "calc(110vh)" }}
              transition={{
                delay: 0.3 * (i + 1),
                duration: 1.5,
                ease: ["easeInOut"],
              }}
            >
              <Box
                style={{
                  display: "flex",
                  flexDirection: "row",
                  border: "2px solid white",
                  borderRadius: 20,
                  justifyContent: "space-between",
                  width: "calc(80vw)",
                  marginBottom: 5,
                  marginTop: 5,
                  paddingLeft: 20,
                  paddingRight: 20,
                  paddingTop: 5,
                  paddingBottom: 5,
                }}
              >
                <DynamicText style={{ width: "30%", fontSize: 20 }}>
                  {p.Name}
                </DynamicText>
                <DynamicText style={{ width: "40%", fontSize: 20 }}>
                  {p.Lie}
                </DynamicText>
                <DynamicText style={{ width: "30%", fontSize: 20 }}>
                  {p.Points}
                </DynamicText>
              </Box>
            </motion.div>
          );
        })}
      </>
    );
  };
  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        height: "calc(98vh)",
        justifyContent: "space-between",
        alignContent: "center",
        textAlign: "center",
        alignItems: state === "ShowGuesses" ? undefined : "center",
      }}
    >
      <DynamicText
        style={{
          color: theme.ButtonColor,
          fontSize: 15,
        }}
      >
        {round}
      </DynamicText>
      {state === "ShowGuesses" ? showGuesses() : showScores()}
      <PlayerReminder reminder={getReminder()} theme={theme} />
    </Box>
  );
};

export default ResultPresentation;
